import React, { useEffect, useState } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Divider,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useUserSearchContext } from 'context/UserSearchContext';
import { TermsAndConditions } from 'components';
import { countries } from 'services/dummy';
import { customerInformationSchema } from 'schema/customerInformationSchema';
import { CustomerInformation } from 'types/CustomerInformation';
export const CustomerInformationForm: React.FC = () => {
  const isPortrait = useMediaQuery('(orientation: portrait)');
  const { bookingDetailId } = useParams();
  const [searchParams] = useSearchParams();
  const locationCode = searchParams.get('locationCode');
  const navigate = useNavigate();

  if (!bookingDetailId || !locationCode) {
    navigate(-1);
  }
  const { bookingSearchData, setBookingSearchData } = useUserSearchContext();
  const [isOpenTermsAndCondition, setIsOpenTermsAndCondition] =
    useState<boolean>(false);

  const resolver: Resolver<CustomerInformation> = yupResolver(
    customerInformationSchema
  );
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<CustomerInformation>({
    resolver,
    defaultValues: {
      FirstName: '',
      LastName: '',
      Email: '',
      MobileNo: '', // keep as string if using regex in schema
      Nationality: '',
      VisaStatus: '' as 'Resident' | 'Visitor',
      IsAbove21: false,
      IsTCAccepted: false,
    },
  });

  const popupHandler = (isOpenStatus: boolean) => {
    setIsOpenTermsAndCondition(isOpenStatus);
  };

  const onSubmit = (data: CustomerInformation) => {
    setBookingSearchData((prev) => ({
      ...prev,
      SearchDetailId: bookingDetailId,
      Renter: {
        Id: '',
        FirstName: data.FirstName,
        LastName: data.LastName,
        EmailId: data.Email,
        ContactNo: Number(data.MobileNo),
        Nationality: data.Nationality,
        VisaStatus: data.VisaStatus,
        IsAbove21: data.IsAbove21,
        IsTCAccepted: data.IsTCAccepted,
      },
    }));
    navigate(`/payment-option/${bookingDetailId}?locationCode=${locationCode}`);
  };
  useEffect(() => {
    if (bookingSearchData.Renter) {
      reset({
        FirstName: bookingSearchData.Renter.FirstName,
        LastName: bookingSearchData.Renter.LastName,
        Email: bookingSearchData.Renter.EmailId,
        MobileNo: String(
          bookingSearchData.Renter.ContactNo
            ? bookingSearchData.Renter.ContactNo
            : ''
        ),
        Nationality: bookingSearchData.Renter.Nationality,
        VisaStatus: bookingSearchData.Renter.VisaStatus,
        IsAbove21: bookingSearchData.Renter.IsAbove21,
        IsTCAccepted: bookingSearchData.Renter.IsTCAccepted,
      });
    }
  }, [bookingSearchData]);
  return isOpenTermsAndCondition ? (
    <TermsAndConditions
      isOpen={isOpenTermsAndCondition}
      popupHandler={popupHandler}
    />
  ) : (
    <Box className="formSection">
      <Box className="formBox" width={isPortrait ? '90%' : '60%'}>
        <Typography className="formText" color="#0a3551" component="p">
          details. Pay. get the car
        </Typography>
        <Divider sx={{ backgroundColor: 'lightgray', height: '1px' }} />
        <form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off">
          <Grid container spacing={2}>
            {/* First Name */}
            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor="FirstName" className="label-text">
                First Name*
              </InputLabel>
              <input
                id="FirstName"
                {...register('FirstName')}
                placeholder="First Name"
                className="input-fields"
                autoComplete="off"
              />
              {errors.FirstName && (
                <Typography color="error" variant="caption">
                  {errors.FirstName.message}
                </Typography>
              )}
            </Grid>

            {/* Last Name */}
            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor="LastName" className="label-text">
                Last Name*
              </InputLabel>
              <input
                id="LastName"
                {...register('LastName')}
                placeholder="Last Name"
                className="input-fields"
                autoComplete="off"
              />
              {errors.LastName && (
                <Typography color="error" variant="caption">
                  {errors.LastName.message}
                </Typography>
              )}
            </Grid>

            {/* Email */}
            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor="Email" className="label-text">
                Email*
              </InputLabel>
              <input
                id="Email"
                {...register('Email')}
                placeholder="Email"
                type="email"
                className="input-fields"
                autoComplete="off"
              />
              {errors.Email && (
                <Typography color="error" variant="caption">
                  {errors.Email.message}
                </Typography>
              )}
            </Grid>

            {/* Mobile Number */}
            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor="MobileNo" className="label-text">
                Mobile No*
              </InputLabel>
              <input
                id="MobileNo"
                {...register('MobileNo')}
                placeholder="Mobile Number"
                type="tel"
                className="input-fields"
                autoComplete="off"
              />
              {errors.MobileNo && (
                <Typography color="error" variant="caption">
                  {errors.MobileNo.message}
                </Typography>
              )}
            </Grid>

            {/* Nationality */}
            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor="Nationality" className="label-text">
                Nationality*
              </InputLabel>
              <Select
                displayEmpty
                id="Nationality"
                {...register('Nationality')}
                value={watch('Nationality') || ''}
                defaultValue=""
                className="input-fields"
              >
                <MenuItem value="" disabled>
                  Select
                </MenuItem>
                {countries &&
                  countries.map((nationality: string, index: number) => {
                    return (
                      <MenuItem key={index} value={`${nationality}`}>
                        {nationality}
                      </MenuItem>
                    );
                  })}
              </Select>
              {errors.Nationality && (
                <Typography color="error" variant="caption">
                  {errors.Nationality.message}
                </Typography>
              )}
            </Grid>

            {/* Visa Status */}
            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor="VisaStatus" className="label-text">
                Visa Status*
              </InputLabel>
              <Select
                displayEmpty
                id="VisaStatus"
                {...register('VisaStatus')}
                value={watch('VisaStatus') || ''}
                defaultValue=""
                className="input-fields"
              >
                <MenuItem value="" disabled>
                  Select
                </MenuItem>
                <MenuItem value="Resident">Resident</MenuItem>
                <MenuItem value="Visitor">Visitor</MenuItem>
              </Select>
              {errors.VisaStatus && (
                <Typography color="error" variant="caption">
                  {errors.VisaStatus.message}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <Box className="form-field checkbox-field">
                <input
                  id="IsAbove21"
                  {...register('IsAbove21')}
                  type="checkbox"
                  className="checkbox-input"
                />
                <InputLabel htmlFor="IsAbove21" className="label-text">
                  Are you above 21?
                </InputLabel>
                {errors.IsAbove21 && (
                  <Typography color="error" variant="caption">
                    {errors.IsAbove21.message}
                  </Typography>
                )}
              </Box>
              <Box className="form-field checkbox-field">
                <input
                  id="IsTCAccepted"
                  {...register('IsTCAccepted')}
                  type="checkbox"
                  className="checkbox-input"
                />
                <InputLabel htmlFor="IsTCAccepted" className="label-text">
                  Accept Terms & Conditions.{' '}
                  <a
                    style={{
                      cursor: 'pointer',
                    }}
                    onClick={() => popupHandler(!isOpenTermsAndCondition)}
                  >
                    Read Here
                  </a>
                </InputLabel>
                {errors.IsTCAccepted && (
                  <Typography color="error" variant="caption">
                    {errors.IsTCAccepted.message}
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* Submit */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            color="primary"
          >
            Submit
          </Button>
        </form>
      </Box>
    </Box>
  );
};
